import { useState, type MouseEvent } from 'react'
import { format, formatDistanceStrict } from 'date-fns'
import { ChevronRight } from 'lucide-react'
import type { Account } from '../../data/types'
import type { InstanceRowModel, InstanceStatus } from '../../data/instances'
import { BLOCK_SECONDS } from '../../core/pricing'
import { formatCores } from '../../core/sizing'
import { StatusPill, type StatusTone } from '../../ui/StatusPill'
import { Button } from '../../ui/Button'
import { EmptyState } from '../../ui/Feedback'
import { Money } from '../../ui/Money'
import { Rate } from '../../ui/Rate'
import { PageHeader } from '../shared/PageHeader'
import { DataGuard, type QueryLike } from '../shared/DataGuard'

export type InstanceView = 'active' | 'closed' | 'all'

/** The enrichment sources that can fail without failing the list. */
export type InstanceSource = 'catalog' | 'deployments' | 'escrow'

/** Rendered at once; the rest wait behind "Show more". */
export const PAGE_SIZE = 200

const GIB = 1024 ** 3

const PILL: Record<InstanceStatus, { tone: StatusTone; label: string }> = {
  running: { tone: 'positive', label: 'Running' },
  'out-of-funds': { tone: 'critical', label: 'Out of funds' },
  waiting: { tone: 'caution', label: 'Waiting for offers' },
  unknown: { tone: 'neutral', label: 'Status unknown' },
  closed: { tone: 'neutral', label: 'Closed' },
}

const SOURCE_LABEL: Record<InstanceSource, string> = {
  catalog: 'hosts',
  deployments: 'resources, waiting requests',
  escrow: 'balances',
}

export function viewFromSearch(search: string): InstanceView {
  const view = new URLSearchParams(search).get('view')
  return view === 'closed' || view === 'all' ? view : 'active'
}

function inView(row: InstanceRowModel, view: InstanceView): boolean {
  if (view === 'all') return true
  return view === 'closed' ? row.status === 'closed' : row.status !== 'closed'
}

function matches(row: InstanceRowModel, query: string): boolean {
  if (!query) return true
  return [row.label.name, row.image ?? '', row.host?.name ?? ''].some((field) => field.toLowerCase().includes(query))
}

function targetOf(row: InstanceRowModel): string {
  const dseq = encodeURIComponent(row.dseq)
  if (row.status === 'waiting') return `/app/rent/offers/${dseq}`
  return row.appTab ? `/app/instances/${dseq}?tab=app` : `/app/instances/${dseq}`
}

/** "6 GB", "0.5 GB", "112 GB": GiB, as the configure page counts them. */
function gb(bytes: number): string {
  return `${Number((bytes / GIB).toFixed(1))} GB`
}

/** The amounts, each kept on one line: a narrow column wraps between them, never inside one. */
export function resourcesText(resources: InstanceRowModel['resources']): string {
  if (!resources) return '—'
  const parts = [formatCores(resources.cpuMilli), gb(resources.memoryBytes), gb(resources.storageBytes)]
  if (resources.replicas > 1) parts.push(`× ${resources.replicas} replicas`)
  return parts.map((part) => part.replaceAll(' ', '\u00a0')).join(' · ')
}

/**
 * A block height as a time. An estimate — the chain's block time drifts — so
 * every rendering of one says "About" in its title.
 */
function timeAt(height: number | null, chainHeight: number | null, now: Date): Date | null {
  if (height == null || !chainHeight) return null
  return new Date(now.getTime() - (chainHeight - height) * BLOCK_SECONDS * 1000)
}

function About({ date, children }: { date: Date; children: string }) {
  return <time dateTime={date.toISOString()} title={`About ${format(date, 'MMM d, yyyy HH:mm')}`}>{children}</time>
}

function StatusFact({ row, chainHeight, now }: { row: InstanceRowModel; chainHeight: number | null; now: Date }) {
  switch (row.status) {
    case 'running': {
      const started = timeAt(row.startedHeight, chainHeight, now)
      return started ? <>Started <About date={started}>{formatDistanceStrict(started, now, { addSuffix: true })}</About></> : null
    }
    case 'out-of-funds': {
      if (!row.runwayBlocks) return <>Closed — ran out of funds</>
      const end = new Date(now.getTime() + row.runwayBlocks * BLOCK_SECONDS * 1000)
      return <>Runs out <About date={end}>{`in ${formatDistanceStrict(end, now)}`}</About></>
    }
    case 'waiting':
      return row.held ? <><Money coin={row.held} /> held</> : null
    case 'unknown':
      return <>Open it to check with the host</>
    case 'closed': {
      const ended = timeAt(row.endedHeight, chainHeight, now)
      if (!ended) return null
      const started = timeAt(row.startedHeight, chainHeight, now)
      const ran = started && started <= ended ? ` · ran ${formatDistanceStrict(started, ended)}` : ''
      return <><About date={ended}>{`Ended ${format(ended, 'MMM d')}`}</About>{ran}</>
    }
  }
}

function RateCell({ row }: { row: InstanceRowModel }) {
  if (row.status === 'waiting') return <>—</>
  if (row.status === 'closed') return <span className="instance-rate-past">was <Rate coin={row.rate} /></span>
  if (row.status === 'unknown' && !row.rate) return <>—</>
  return <Rate coin={row.rate} />
}

function InstanceRow({ row, chainHeight, now, onNavigate }: { row: InstanceRowModel; chainHeight: number | null; now: Date; onNavigate?: ((path: string) => void) | undefined }) {
  const target = targetOf(row)
  const pill = PILL[row.status]
  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    // Let the browser handle a new-tab or new-window click.
    if (!onNavigate || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    event.preventDefault()
    onNavigate(target)
  }
  return <tr className={`instance-row instance-row--${row.status}`}>
    <td className="instance-cell-name">
      <a className={`instance-link${row.label.source === 'fallback' ? ' instance-name--fallback' : ''}`} href={target} aria-label={`Open ${row.label.name}`} onClick={open}>{row.label.name}</a>
      <span className={`instance-image${row.image ? '' : ' instance-image--missing'}`} title={row.image ?? undefined}>{row.image ?? 'Image not recorded'}</span>
    </td>
    <td data-label="Host"><div>{row.host ? <><span>{row.host.name}</span>{row.host.region && <span className="instance-sub">{row.host.region}</span>}</> : <span className="instance-sub">No host yet</span>}</div></td>
    <td data-label="Resources"><div>{resourcesText(row.resources)}</div></td>
    <td data-label="Status"><div><StatusPill tone={pill.tone} label={pill.label} /><span className="instance-sub"><StatusFact row={row} chainHeight={chainHeight} now={now} /></span></div></td>
    <td data-label="Rate" className="instance-cell-rate"><div><RateCell row={row} /></div></td>
    <td className="instance-cell-chevron" aria-hidden="true"><ChevronRight size={16} /></td>
  </tr>
}

function InstanceList({ rows, chainHeight, now, onNavigate }: { rows: readonly InstanceRowModel[]; chainHeight: number | null; now: Date; onNavigate?: ((path: string) => void) | undefined }) {
  const [limit, setLimit] = useState(PAGE_SIZE)
  return <>
    <div className="instance-table-wrap">
      <table className="instance-table">
        <caption className="sr-only">Your instances</caption>
        <thead className="instance-table-head">
          <tr>
            <th scope="col">Instance</th>
            <th scope="col">Host</th>
            <th scope="col">Resources</th>
            <th scope="col">Status</th>
            <th scope="col" className="instance-cell-rate">Rate</th>
            <th scope="col"><span className="sr-only">Open</span></th>
          </tr>
        </thead>
        <tbody>{rows.slice(0, limit).map((row) => <InstanceRow key={row.dseq} row={row} chainHeight={chainHeight} now={now} onNavigate={onNavigate} />)}</tbody>
      </table>
    </div>
    {rows.length > limit && <div className="instance-more"><Button onClick={() => setLimit((current) => current + PAGE_SIZE)}>Show more</Button><span className="muted">Showing {limit} of {rows.length}</span></div>}
  </>
}

function InstanceToolbar({ view, counts, search, onView, onSearch }: { view: InstanceView; counts: Record<InstanceView, number>; search: string; onView: (view: InstanceView) => void; onSearch: (value: string) => void }) {
  const views: Array<[InstanceView, string]> = [['active', 'Active'], ['closed', 'Closed'], ['all', 'All']]
  return <div className="instance-toolbar">
    <div className="instance-views" role="group" aria-label="Show instances">
      {views.map(([value, label]) => <button key={value} type="button" className="instance-view" aria-pressed={view === value} onClick={() => onView(value)}>{label} <span className="instance-view-count">{counts[value]}</span></button>)}
    </div>
    <input className="instance-search" type="search" aria-label="Search instances" placeholder="Search by name, image or host" value={search} onChange={(event) => onSearch(event.target.value)} />
  </div>
}

export interface InstancesPageProps {
  account: Account | null
  /** The list, keyed to the leases query: its failure is the page's only full failure. */
  query: QueryLike<InstanceRowModel[]>
  view?: InstanceView | undefined
  chainHeight?: number | null | undefined
  /** Enrichment sources that failed; each degrades its own column only. */
  failedSources?: readonly InstanceSource[] | undefined
  onRetrySources?: (() => void) | undefined
  onConnect?: (() => void) | undefined
  onNavigate?: ((path: string) => void) | undefined
  /** Injected by tests; the page uses the current time. */
  now?: Date | undefined
}

export function InstancesPage({ account, query, view = 'active', chainHeight = null, failedSources = [], onRetrySources, onConnect, onNavigate, now = new Date() }: InstancesPageProps) {
  const [search, setSearch] = useState('')
  if (!account) return <EmptyState level={1} title="Connect a wallet to see your instances" body="Your instances belong to the wallet address you connect." action={onConnect ? <Button variant="primary" onClick={onConnect}>Connect wallet</Button> : undefined} />
  const rent = <Button variant="primary" onClick={() => onNavigate?.('/app/rent')}>Rent a machine</Button>
  const showView = (next: InstanceView) => onNavigate?.(next === 'active' ? '/app/instances' : `/app/instances?view=${next}`)
  return <>
    <PageHeader eyebrow="Workspace" title="Instances" description="Everything you have rented, running or not." actions={rent} />
    {failedSources.length > 0 && <div className="notice notice-caution instance-degraded" role="status">
      <span>Some details could not be loaded ({failedSources.map((source) => SOURCE_LABEL[source]).join(', ')}).{failedSources.includes('deployments') ? '' : ' The list is still accurate.'}</span>
      {onRetrySources && <Button size="sm" onClick={onRetrySources}>Retry</Button>}
    </div>}
    <DataGuard query={query} loading={<div className="skeleton-stack"><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div>} empty={{ title: 'No instances yet', body: 'Rent a machine and it will appear here.', action: rent }}>{(rows) => {
      const counts: Record<InstanceView, number> = { active: rows.filter((row) => inView(row, 'active')).length, closed: rows.filter((row) => inView(row, 'closed')).length, all: rows.length }
      const needle = search.trim().toLowerCase()
      const inTab = rows.filter((row) => inView(row, view))
      const shown = inTab.filter((row) => matches(row, needle))
      return <>
        <InstanceToolbar view={view} counts={counts} search={search} onView={showView} onSearch={setSearch} />
        {shown.length > 0
          ? <InstanceList key={`${view}:${needle}`} rows={shown} chainHeight={chainHeight} now={now} onNavigate={onNavigate} />
          : inTab.length > 0
            ? <EmptyState title={`No instances match "${search.trim()}".`} body="Search looks at names, images and hosts." action={<Button onClick={() => setSearch('')}>Clear search</Button>} />
            : view === 'active'
              ? <EmptyState title="Nothing is running" body={`Your ${counts.closed} closed instance${counts.closed === 1 ? ' is' : 's are'} under Closed.`} action={<Button onClick={() => showView('closed')}>Show closed instances</Button>} />
              : <EmptyState title="No closed instances" body="Instances you close are listed here." />}
      </>
    }}</DataGuard>
  </>
}
